import React from "react";
//#region types
import { NavigationLayoutProps, NavLayoutMenuItem } from "models/propTypes/pageLayoutPropTypes";
import { RouteConstants } from "utils/constants";
//#endregion
//#region components
import NavigationLayoutMenu from "./navLayoutMenu";
import { Grid } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { scrollContainerToAnchor } from "utils/helpers";

const NavigationLayout: React.FC<NavigationLayoutProps> = (props) => {
  const [sections, setSections] = React.useState<NavLayoutMenuItem[]>();
  const [selectedMenuIndex, setSelectedMenuIndex] = React.useState<number>(0);
  const [selectedSubMenuIndex, setSelectedSubMenuIndex] = React.useState<number>(0);
  const [lastClickTime, setLastClickTime] = React.useState(0);

  // Deep-link support: a URL hash (e.g. ?section=customerSettings#reference) should scroll to the
  // matching sub-section once its content is rendered. Captured once on the initial render so it
  // survives the query-param-only navigations performed while the top section is being selected.
  const initialHashRef = React.useRef(window.location.hash.replace("#", ""));
  const hashHandledRef = React.useRef(false);

  // Private function to handle debouncing for navigation clicks
  const shouldAllowNavigation = () => {
    const now = Date.now();
    if (now - lastClickTime < 300) return false;
    setLastClickTime(now);
    return true;
  };

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const selectedSection = queryParams.get(RouteConstants.QueryParamStrings.Section) ?? "";

  React.useEffect(() => {
    const subSectionNavigation = sections?.length === 1;

    const selectedSectionIndex = (subSectionNavigation ? sections![0].subMenuItems : sections)?.findIndex(
      (s) => s.key === selectedSection
    );
    if (
      selectedSectionIndex !== undefined &&
      selectedSectionIndex > -1 &&
      (subSectionNavigation
        ? selectedSubMenuIndex !== selectedSectionIndex
        : selectedMenuIndex !== selectedSectionIndex)
    ) {
      subSectionNavigation ? onSubMenuItemSelect(selectedSectionIndex) : onMenuItemSelect(selectedSectionIndex);
    }
  }, [selectedSection, sections]);

  React.useEffect(() => {
    if (sections === undefined)
      setSections(
        props.navItems.map(
          (navItem, index): NavLayoutMenuItem => ({
            key: navItem.navKey,
            label: navItem.title,
            icon: navItem.icon,
            menuSectionHeader: navItem.menuSectionHeader,
          })
        )
      );
  }, [props]);

  // Scroll to the deep-linked sub-section once it has been registered and its element is in the DOM.
  React.useEffect(() => {
    if (hashHandledRef.current) return;

    const anchorKey = initialHashRef.current;
    if (!anchorKey) return;

    const subMenuItems = sections?.[selectedMenuIndex]?.subMenuItems;
    const subMenuIndex = subMenuItems?.findIndex((s) => s.key === anchorKey) ?? -1;
    if (subMenuIndex < 0 || !document.getElementById(anchorKey)) return;

    hashHandledRef.current = true;
    setSelectedSubMenuIndex(subMenuIndex);
    scrollContainerToAnchor(anchorKey, { behavior: "smooth", block: "start" });
  }, [sections, selectedMenuIndex]);

  const setNavLayoutSections = React.useCallback((subSections: NavLayoutMenuItem[]) => {
    setSections((prevSections) => {
      if (!prevSections) return prevSections;
      const updatedSections = [...prevSections];
      updatedSections[selectedMenuIndex].subMenuItems = subSections;
      return updatedSections;
    });
  }, [selectedMenuIndex]);

  const onSubMenuItemSelect = (index: number) => {
    if (selectedSubMenuIndex !== index) {
      // Only debounce clicks that will actually cause navigation
      if (!shouldAllowNavigation()) return;
    }

    setSelectedSubMenuIndex(index);
    const subMenuItems = sections ? sections[selectedMenuIndex].subMenuItems : undefined;
    const selectedSubMenu = subMenuItems ? subMenuItems[index] : undefined;
    props.onSubMenuItemSelectHandler && props.onSubMenuItemSelectHandler(selectedSubMenu?.label);

    if (selectedSubMenu) {
      // Use advanced scroll logic with delay for animated content (wizards)
      if (props.enableDelayedScrollToTarget) {
        setTimeout(() => {
          scrollContainerToAnchor(selectedSubMenu.key, { behavior: "smooth", block: "start" });
        }, 50);
      } else {
        // Immediate scrolling for normal pages
        scrollContainerToAnchor(selectedSubMenu.key, { behavior: "smooth", block: "start" });
      }

      if (sections?.length === 1) {
        const queryParams = new URLSearchParams({
          [RouteConstants.QueryParamStrings.Section]: selectedSubMenu.key,
        }).toString();
        navigate(`${location.pathname}?${queryParams}`, { replace: true });
      }
    }
  };

  React.useEffect(() => {
    props.onMenuItemSelectHandler && props.onMenuItemSelectHandler(props.navItems[0]?.title);
  }, []);

  const onMenuItemSelect = (index: number) => {
    if (selectedMenuIndex !== index) {
      // Only debounce clicks that will actually cause navigation
      if (!shouldAllowNavigation()) return;

      const navItem = props.navItems[index];

      setSelectedMenuIndex(index);
      setSelectedSubMenuIndex(0);
      props.onMenuItemSelectHandler && props.onMenuItemSelectHandler(navItem?.title);

      const queryParams = new URLSearchParams({
        [RouteConstants.QueryParamStrings.Section]: navItem?.navKey,
      }).toString();
      navigate(`${location.pathname}?${queryParams}`, { replace: true });
    }
  };

  const container = React.useMemo(() => props.navItems[selectedMenuIndex].content, [selectedMenuIndex]);

  const shouldHideNav = props.hideNavOnNarrowScreens ?? false;

  return (
    <Grid 
      item 
      container 
      spacing={5} 
      className={clsx(cssExports.navLayout, shouldHideNav && cssExports.hideNavOnNarrowScreens)}
      flexWrap="nowrap"
    >
      <Grid item>
        <NavigationLayoutMenu
          menuItems={sections ?? []}
          onMenuItemSelect={onMenuItemSelect}
          onSubMenuItemSelect={onSubMenuItemSelect}
          selectedMenuIndex={selectedMenuIndex}
          selectedSubMenuIndex={selectedSubMenuIndex}
        />
      </Grid>
      <Grid item container xs className={cssExports.scrollLayoutItemContainer}>
        {sections && container && (
          <container.type
            {...container.props}
            key={selectedMenuIndex}
            setNavLayoutSections={setNavLayoutSections}
            title={container.props.title ?? props.navItems[selectedMenuIndex].title}
          ></container.type>
        )}
      </Grid>
    </Grid>
  );
};

export default NavigationLayout;
